import { isBrowsing } from './browse-mode.ts';
import { $ } from './dom.ts';
import {
  type AssignmentConfig,
  type Message,
  ReportedError,
  type RunnerResponse,
  type SiteMetadata,
} from './evaluator-types.ts';
import { describe, getText, postJsonWithRetry } from './fetch-helpers.ts';
import { pageLocation } from './storage.ts';

// getCode/getConfig are installed on our window for the page's harness
// scripts (running in the evaluation iframe, reaching up via window.parent).
type HarnessWindow = Window & {
  getCode: () => string | null;
  getConfig: () => AssignmentConfig;
};

const harnessWindow = window as unknown as HarnessWindow;

class ServerEvaluator {
  stdin: HTMLInputElement | null;
  stdout: HTMLElement | null;
  stderr: HTMLElement | null;
  config: AssignmentConfig & { server: NonNullable<AssignmentConfig['server']> };
  message: Message;
  serverUrl: string | null | undefined;

  constructor(
    config: AssignmentConfig & { server: NonNullable<AssignmentConfig['server']> },
    message: Message,
    _username: string,
    siteMetadata: SiteMetadata,
  ) {
    const { stdin, stdout, stderr } = config;
    this.stdin = stdin ? ($(`#${stdin}`) as HTMLInputElement | null) : null;
    // most pages only have a #stderr so send stdout there too.
    this.stdout = stdout
      ? ($(`#${stdout}`) as HTMLElement | null)
      : ($('#stderr') as HTMLElement | null);
    this.stderr = stderr
      ? ($(`#${stderr}`) as HTMLElement | null)
      : ($('#stderr') as HTMLElement | null);
    this.config = config;
    this.message = message;
    this.serverUrl = siteMetadata.serverUrl;
    harnessWindow.getCode = () => null;
    harnessWindow.getConfig = () => this.config;
  }

  // The contents of the assignment's other editor tabs, set per execute()
  // call. Sent to the server along with the static config.extraFiles.
  #otherSources: string[] = [];

  // A missing extra source is a visible "Couldn't load <file> (404)" in the
  // minibuffer, never a 404 page compiled as Java.
  async #fetchExtraSources(): Promise<string[]> {
    const files: string[] = this.config.extraFiles ?? [];
    const fetched = await Promise.all(
      files.map((f) =>
        getText(f).catch((e) => {
          throw new Error(describe(e, `load ${f}`));
        }),
      ),
    );
    return [...this.#otherSources, ...fetched];
  }

  // A failure the student has now seen in the minibuffer (red); the thrown
  // ReportedError tells the caller not to report it again.
  #fail(text: string): never {
    this.message(text, undefined, 'error');
    throw new ReportedError(text);
  }

  // 503/502-retrying POST (runner pool saturation / a runner machine
  // stopped under the request; see fetch-helpers.ts).
  async #postJson(url: string, body: string): Promise<RunnerResponse> {
    return postJsonWithRetry(url, body, this.message);
  }

  async execute(
    code: string,
    _filename?: string,
    others: { file: string; content: string }[] = [],
  ) {
    // '' is a valid serverUrl meaning same-origin (the dev-mode runner
    // proxy); only null/undefined mean there's no runner at all.
    if (this.serverUrl == null) {
      this.#fail('No test server configured (SERVER_URL is unset).');
    }
    this.#otherSources = others.map((o) => o.content);
    this.stdout?.replaceChildren();
    this.stderr?.replaceChildren();

    if (this.config.server.graphic) {
      return this.#executeGraphic(code);
    }

    if (this.config.server.canvas) {
      return this.#executeCanvas(code);
    }

    if (this.config.server.run) {
      return this.#executeRun(code);
    }

    const start = performance.now();
    this.message('Submitting job ...');

    const { testClass } = this.config.server;
    const extraSources = await this.#fetchExtraSources();
    const url = `${this.serverUrl}/api/submissions/`;
    // courseId/assignment identify the run for the server-side test_runs
    // record (the submission proxy); the runner itself ignores them. A run
    // while browsing an old revision is off the record — the proxy skips
    // recording (plans/delete-rename-files.md).
    const { courseId, dir } = pageLocation();
    const body = JSON.stringify({
      testClass,
      code,
      ...(extraSources.length ? { extraSources } : {}),
      ...(courseId && dir ? { courseId, assignment: dir } : {}),
      ...(isBrowsing() ? { browse: true } : {}),
    });

    let response: RunnerResponse;
    try {
      response = await this.#postJson(url, body);
    } catch (e) {
      this.#fail(describe(e, 'submit your code'));
    }

    const { status, results, explanation, stdout, stderr } = response;
    const time = Math.round(performance.now() - start);

    if (status === 200) {
      this.message(`Job complete. (${time} millis.)`);
      if (stdout) this.stdout?.replaceChildren($('<pre>', stdout) as HTMLElement);
      if (stderr) this.stderr?.replaceChildren($('<pre>', stderr) as HTMLElement);
      return results;
    } else if (status === 400) {
      this.#fail('Time limit exceeded.');
    } else if (status === 422) {
      const { errorType } = response;
      this.stderr?.replaceChildren($('<pre>', explanation) as HTMLElement);
      this.#fail(errorType === 'compile' ? 'Compile error.' : 'Runtime error.');
    } else if (status === 503) {
      this.#fail('Server overloaded. Please try again later.');
    } else {
      console.error(`Unexpected runner status ${status}:`, explanation);
      this.#fail(`Unknown error (status ${status}).`);
    }
  }

  async #executeRun(code: string) {
    this.message('Submitting job ...');
    const start = performance.now();
    const extraSources = await this.#fetchExtraSources();
    const url = `${this.serverUrl}/api/run/`;
    const stdin = this.stdin?.value || null;
    const body = JSON.stringify({
      code,
      ...(extraSources.length ? { extraSources } : {}),
      ...(stdin ? { stdin } : {}),
    });

    let response: RunnerResponse;
    try {
      response = await this.#postJson(url, body);
    } catch (e) {
      this.#fail(describe(e, 'submit your code'));
    }

    const { status, explanation, stdout, stderr } = response;
    const time = Math.round(performance.now() - start);

    if (status === 200) {
      this.message(`Job complete. (${time} millis.)`);
      if (stdout) this.stdout?.replaceChildren($('<pre>', stdout) as HTMLElement);
      if (stderr) this.stderr?.replaceChildren($('<pre>', stderr) as HTMLElement);
      return null;
    } else if (status === 400) {
      this.#fail('Time limit exceeded.');
    } else if (status === 422) {
      const { errorType } = response;
      this.stderr?.replaceChildren($('<pre>', explanation) as HTMLElement);
      this.#fail(errorType === 'compile' ? 'Compile error.' : 'Runtime error.');
    } else if (status === 503) {
      this.#fail('Server overloaded. Please try again later.');
    } else {
      console.error(`Unexpected runner status ${status}:`, explanation);
      this.#fail(`Unknown error (status ${status}).`);
    }
  }

  async #executeCanvas(code: string) {
    this.message('Submitting job ...');
    const start = performance.now();
    const iframe = $<HTMLIFrameElement>('iframe');
    const rect = iframe.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const extraSources = await this.#fetchExtraSources();
    const url = `${this.serverUrl}/api/canvas/`;
    const body = JSON.stringify({
      code,
      width,
      height,
      ...(extraSources.length ? { extraSources } : {}),
    });

    let response: RunnerResponse;
    try {
      response = await this.#postJson(url, body);
    } catch (e) {
      this.#fail(describe(e, 'submit your code'));
    }

    const { status, code: jsCode, explanation, errorType } = response;
    if (status === 200) {
      const time = Math.round(performance.now() - start);
      this.message(`Done. (${time} millis.)`);
      return jsCode;
    } else if (errorType === 'timeout') {
      this.#fail('Time limit exceeded.');
    } else if (status === 503) {
      this.#fail('Server overloaded. Please try again later.');
    } else {
      this.stderr?.replaceChildren($('<pre>', explanation) as HTMLElement);
      this.#fail(errorType === 'compile' ? 'Compile error.' : 'Runtime error.');
    }
  }

  async #executeGraphic(code: string) {
    this.message('Submitting job ...');
    const start = performance.now();
    harnessWindow.getCode = () => code;
    const img = $('div.graphic img') as HTMLElement;
    const width = Math.floor(img.clientWidth);
    const height = Math.floor(img.clientHeight);
    const extraSources = await this.#fetchExtraSources();
    const url = `${this.serverUrl}/api/graphics/`;
    const body = JSON.stringify({
      code,
      width,
      height,
      ...(extraSources.length ? { extraSources } : {}),
    });

    let response: RunnerResponse;
    try {
      response = await this.#postJson(url, body);
    } catch (e) {
      this.#fail(describe(e, 'submit your code'));
    }

    const { status, image, explanation, errorType } = response;
    if (status === 200) {
      const time = Math.round(performance.now() - start);
      this.message(`Done. (${time} millis.)`);
      return image;
    } else if (errorType === 'timeout') {
      this.#fail('Time limit exceeded.');
    } else if (status === 503) {
      this.#fail('Server overloaded. Please try again later.');
    } else {
      this.stderr?.replaceChildren($('<pre>', explanation) as HTMLElement);
      this.#fail(errorType === 'compile' ? 'Compile error.' : 'Runtime error.');
    }
  }
}

const evaluator = (
  config: AssignmentConfig & { server: NonNullable<AssignmentConfig['server']> },
  message: Message,
  username: string,
  metadata: SiteMetadata,
) => new ServerEvaluator(config, message, username, metadata);

export default evaluator;
